/**
 * AIMAP-03 (Wave 2) — PUT /api/taxonomy/v1/defect-catalogues/:catalogueId/ai-map/:classUuid
 * (`suggestCatalogueAiMapping`, taxonomy/v1@2.2.0).
 *
 * Replaces the retired lyse `PATCH /mappers/:catalogueId/:aiClassName`
 * (keyed on the per-model `aiClassName`, which is NOT globally unique). The
 * "apply this mapping to future findings" affordance in `<CataloguePopover/>`
 * now keys on the authoritative `classUuid` (`ai_finding_model_classes`) and
 * writes one `(classUuid → typeEntryId)` row into the catalogue's OPEN DRAFT:
 *
 *   - `applyImmediately:false` (Suggest) — the draft is left for an admin/expert
 *     to publish; available to pre_screener / expert_analyst (+ admins).
 *   - `applyImmediately:true` (Apply immediately) — creates the draft, writes the
 *     rule, and publishes in one call; expert_analyst / tenant_admin (+
 *     platform_admin) only (a pre_screener gets `403`).
 *
 * Wiring mirrors the taxonomy admin mutations (`taxonomyQueries.ts`): the host's
 * `useApi()` axios instance is base-pinned to `/api/lyse/v1`, so we override the
 * per-request `baseURL` with `getTaxonomyBaseUrl()` (`tx()`) while keeping the
 * shared auth / tenant / refresh interceptors. The path is relative.
 *
 * Best-effort, per the original AC13 contract: `retry: 0`. A `409`
 * (`draft_already_exists`) is surfaced as a non-blocking message by the caller
 * rather than retried — a draft is already open and an admin must publish or
 * discard it before another suggestion can land.
 */
import { useMutation } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { getTaxonomyBaseUrl } from "@aeriosiq/auth-client";
import type { SuggestAiMapBody, SuggestAiMapResponse } from "@aeriosiq/types";

/** Per-request axios config that re-points the base at the taxonomy gateway. */
function tx(): { baseURL: string } {
  return { baseURL: getTaxonomyBaseUrl() };
}

const enc = encodeURIComponent;

export interface SuggestAiMapVars {
  catalogueId: string;
  /** Authoritative class key (`ai_finding_model_classes.class_uuid`); path param. */
  classUuid: string;
  /** Type-axis leaf (entry_type='defect_type') the class maps to. */
  typeEntryId: string;
  /** When true, publish the draft in the same call (expert/admin only). */
  applyImmediately: boolean;
}

export type SuggestAiMapError = Error & {
  errorId?: string;
  status?: number;
  /** Parsed `reason` from a 409 body (e.g. `'draft_already_exists'`). */
  reason?: string;
};

export function useSuggestAiMapMutation() {
  const api = useApi();
  return useMutation<SuggestAiMapResponse, SuggestAiMapError, SuggestAiMapVars>(
    {
      mutationFn: async ({
        catalogueId,
        classUuid,
        typeEntryId,
        applyImmediately,
      }) => {
        const body: SuggestAiMapBody = { typeEntryId, applyImmediately };
        const res = await api.put<SuggestAiMapResponse>(
          `/defect-catalogues/${enc(catalogueId)}/ai-map/${enc(classUuid)}`,
          body,
          tx(),
        );
        return res.data;
      },
      // Don't retry — best-effort (and a 409 conflict must not be hammered).
      retry: 0,
    },
  );
}
